import ContactsAPI from 'dashboard/api/contacts';

/**
 * Converts an email address into a human-readable name.
 * e.g. "mario.rossi@example.com" → "Mario Rossi"
 */
export function humanizeEmail(email) {
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Finds an existing contact by email or creates a new one.
 * Strategy: in-memory search → API search → create → retry on 422 (duplicate).
 *
 * @param {Object} params
 * @param {string} params.email - Email address to look up / create
 * @param {number} params.inboxId - Inbox ID for contact creation
 * @param {number} params.accountId - Account ID
 * @param {Array}  [params.availableContacts=[]] - In-memory contacts to check first
 * @returns {Promise<number>} Contact ID
 */
export async function findOrCreateContact({
  email,
  inboxId,
  accountId,
  availableContacts = [],
}) {
  const emailLower = email.toLowerCase();

  // 1. Check in-memory contacts
  const existing = availableContacts.find(
    (c) => c.email && c.email.toLowerCase() === emailLower
  );
  if (existing?.id) return existing.id;

  // 2. Search via API (direct call, bypasses store to avoid CLEAR_CONTACTS side effect)
  try {
    const {
      data: { payload },
    } = await ContactsAPI.search(email, 1);
    const found = (payload || []).find(
      (c) => c.email && c.email.toLowerCase() === emailLower
    );
    if (found?.id) return found.id;
  } catch {
    // continue to creation
  }

  // 3. Create contact
  try {
    const resp = await ContactsAPI.create({
      inbox_id: inboxId,
      name: humanizeEmail(email),
      email,
      account_id: accountId,
    });
    const id = resp?.data?.payload?.contact?.id;
    if (id) return id;
    throw new Error('Invalid contact create response');
  } catch (err) {
    // 4. Retry search on 422 (duplicate race condition)
    if (err.response?.status === 422) {
      try {
        const {
          data: { payload },
        } = await ContactsAPI.search(email, 1);
        const found = (payload || []).find(
          (c) => c.email && c.email.toLowerCase() === emailLower
        );
        if (found?.id) return found.id;
      } catch {
        // fall through
      }
    }
    throw err;
  }
}

/**
 * Fire-and-forget batch contact creation for multiple emails.
 * Executes sequentially to avoid race conditions on 422 duplicates.
 * Errors are swallowed — safe for non-blocking usage.
 *
 * @param {string[]} emails - Email addresses to find or create
 * @param {Object} params
 * @param {number} params.inboxId
 * @param {number} params.accountId
 * @param {Array}  [params.availableContacts=[]]
 */
export function findOrCreateContacts(
  emails,
  { inboxId, accountId, availableContacts = [] }
) {
  if (!emails || emails.length === 0) return;

  // Deduplicate, normalize, and validate
  const uniqueEmails = [
    ...new Set(
      emails
        .map((e) => e?.toLowerCase().trim())
        .filter((e) => e && e.includes('@'))
    ),
  ];

  if (uniqueEmails.length === 0) return;

  // Sequential execution to avoid parallel 422 race conditions.
  uniqueEmails.reduce(
    (promise, email) =>
      promise.then(() =>
        findOrCreateContact({
          email,
          inboxId,
          accountId,
          availableContacts,
        }).catch(() => undefined)
      ),
    Promise.resolve()
  );
}
